import { SuperGridValidateFunction, SuperGridValidationEntity } from '@aleph/supergrid';
import { SpotifyLevel } from 'wb-spotify/app/routes/superGrid/utils';
import { validateSpotifyAd } from 'wb-spotify/app/routes/superGrid/validation/ad';
import { validateSpotifyCampaign } from 'wb-spotify/app/routes/superGrid/validation/campaign';
import { string, ValidationError } from 'yup';
import { uniqBy } from 'lodash';
import { SpotifyCreateData, SpotifyCreateLevels } from 'wb-spotify/app/routes/superGrid/mutator/utils';
import { validateFrontendGroupData } from 'wb-spotify/app/routes/superGrid/validation/frontendGroupSchema';

export const transformYupErrors = (errors: ValidationError, level: string): SuperGridValidationEntity[] => uniqBy(errors.inner?.map(
	error => ({
		id:      errors?.value?.id ?? errors?.value?.hash,
		message: error.message,
		// this is here because yup returns the path as "field[0]" for arrays or "field.nestedField" for nested validations
		field:   error.path ?? '',
		level,
	}),
) ?? [], (error) => `${error.id}_${error.field}_${error.level}`);

export const validateSpotifyEntity: SuperGridValidateFunction = async ({ changeEvent, level }) => {
	try {
		switch (level) {
			case SpotifyLevel.GROUP:
				await validateFrontendGroupData({
					...changeEvent.data,
					campaignId:   changeEvent.data.parent.id,
					groupName:    changeEvent.data.name,
					groupTargets: {
						...changeEvent.data.groupTargets,
						// If groupTargetsGeotargets is not set when creating, the BE sets it to an object with countryCode set as null
						// what we need to do is omit the object if countryCode is null
						groupTargetsGeoTargets: changeEvent.data.groupTargets?.groupTargetsGeoTargets?.countryCode ? {
							countryCode: changeEvent.data.groupTargets?.groupTargetsGeoTargets.countryCode,
						} : undefined,
					},
				});

				return [];
			case SpotifyLevel.CAMPAIGN:
				await validateSpotifyCampaign({
					...changeEvent.data,
					campaignName: changeEvent.data.name,
				});

				return [];
			case SpotifyLevel.AD:
				await validateSpotifyAd({
					...changeEvent.data,
					adName: changeEvent.data.name,
				});

				return [];
		}
	} catch (e) {
		if (changeEvent?.node?.id) {
			return transformYupErrors(e as ValidationError, changeEvent?.node?.data?.supergridLevel);
		}
	}

	return [];
};

export const nameValidator = string()
	.required('Name is required')
	.test({
		name:    'name-too-short',
		message: 'Name must be at least 3 characters',
		test:    value => value.length >= 3,
	}).test({
		name:    'name-too-long',
		message: 'Name must not be longer than 200 characters',
		test:    value => value.length <= 200,
	});

export const preValidateSpotifyEntities = async (mutations: Record<SpotifyCreateLevels, SpotifyCreateData[]>) => {
	let preValidations: SuperGridValidationEntity[] = [];

	// eslint-disable-next-line no-restricted-syntax
	for (const campaign of mutations.campaign ?? []) {
		try {
			// eslint-disable-next-line no-await-in-loop
			await validateSpotifyCampaign({ ...campaign, campaignName: campaign.name });
		} catch (errors: unknown) {
			preValidations = [
				...preValidations,
				...transformYupErrors((errors as unknown as ValidationError) ?? [], campaign?.supergridLevel),
			];
		}
	}

	// eslint-disable-next-line no-restricted-syntax
	for (const group of mutations.group ?? []) {
		try {
			// eslint-disable-next-line no-await-in-loop
			await validateFrontendGroupData({ ...group, groupName: group.name });
		} catch (errors: unknown) {
			preValidations = [
				...preValidations,
				...transformYupErrors((errors as unknown as ValidationError) ?? [], group?.supergridLevel),
			];
		}
	}

	// eslint-disable-next-line no-restricted-syntax
	for (const ad of mutations.ad ?? []) {
		try {
			// eslint-disable-next-line no-await-in-loop
			await validateSpotifyAd({ ...ad, adName: ad.name });
		} catch (errors: unknown) {
			preValidations = [
				...preValidations,
				...transformYupErrors((errors as unknown as ValidationError) ?? [], ad?.supergridLevel),
			];
		}
	}

	return preValidations;
};
